import { useRef, useState } from "react";
import { useTilt } from "@/hooks/useTilt";
import { useIntervalWhenVisible } from "@/hooks/useIntervalWhenVisible";
import { useI18n } from "@/i18n/I18nProvider";

const PILLARS = [
    { n: "01", titleKey: "why.p1.title", bodyKey: "why.p1.body", mini: "free" },
    { n: "02", titleKey: "why.p2.title", bodyKey: "why.p2.body", mini: "commission" },
    { n: "03", titleKey: "why.p3.title", bodyKey: "why.p3.body", mini: "direct" },
];

// Mini-demos inside each pillar card
function FreeMini() {
    const { t } = useI18n();
    return (
        <div className="itly-pillar-mini">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <span style={miniLabelStyle}>{t("why.mini.canone")}</span>
                <span
                    style={{
                        marginLeft: "auto",
                        fontFamily: "Fraunces, serif",
                        fontWeight: 700,
                        fontSize: 22,
                        color: "#25A26A",
                        letterSpacing: "-0.01em",
                    }}
                >
                    € 0,00
                </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={miniLabelStyle}>{t("why.mini.fisso")}</span>
                <span
                    style={{
                        marginLeft: "auto",
                        fontFamily: "Fraunces, serif",
                        fontWeight: 700,
                        fontSize: 22,
                        color: "#25A26A",
                        letterSpacing: "-0.01em",
                    }}
                >
                    € 0,00
                </span>
            </div>
        </div>
    );
}

function CommissionMini() {
    const { t } = useI18n();
    const [show, setShow] = useState(0);
    const ref = useRef(null);
    useIntervalWhenVisible(ref, () => setShow((v) => (v + 1) % 2), 1800);
    const showed = show === 0;
    return (
        <div ref={ref} className="itly-pillar-mini" style={{ background: showed ? "var(--itly-surface-green)" : "var(--itly-map-region)", transition: "background 500ms ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span
                    aria-hidden="true"
                    style={{
                        width: 22,
                        height: 22,
                        borderRadius: 999,
                        background: showed ? "#25A26A" : "#B8AF9A",
                        color: "#fff",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                    }}
                >
                    {showed ? (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                    ) : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 6l12 12M6 18L18 6" /></svg>
                    )}
                </span>
                <span style={{ fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, color: "var(--itly-fg-strong)" }}>
                    {showed ? t("why.mini.shown") : t("why.mini.noshow")}
                </span>
                <span
                    style={{
                        marginLeft: "auto",
                        fontFamily: "Fraunces, serif",
                        fontWeight: 700,
                        fontSize: 20,
                        color: "var(--itly-fg-strong)",
                    }}
                >
                    {showed ? "✓" : "€ 0"}
                </span>
            </div>
        </div>
    );
}

function DirectMini() {
    const { t } = useI18n();
    return (
        <div className="itly-pillar-mini">
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--itly-muted)", flexWrap: "wrap" }}>
                <span
                    style={{
                        padding: "4px 10px",
                        background: "var(--itly-surface)",
                        border: "1px solid var(--itly-border)",
                        borderRadius: 999,
                        color: "var(--itly-fg-strong)",
                        fontWeight: 600,
                    }}
                >
                    {t("why.mini.customer")}
                </span>
                <span aria-hidden="true">→</span>
                <span
                    style={{
                        padding: "4px 10px",
                        background: "var(--itly-teal-900)",
                        color: "#F7F4EE",
                        borderRadius: 999,
                        fontWeight: 600,
                    }}
                >
                    {t("why.mini.you")}
                </span>
                <span aria-hidden="true" style={{ opacity: 0.6 }}>{t("why.mini.directPay")}</span>
            </div>
        </div>
    );
}

const MINIS = { free: FreeMini, commission: CommissionMini, direct: DirectMini };

function PillarCard({ p }) {
    const { t } = useI18n();
    const tilt = useTilt(6);
    const Mini = MINIS[p.mini];
    return (
        <div
            ref={tilt.ref}
            onMouseMove={tilt.onMouseMove}
            onMouseLeave={tilt.onMouseLeave}
            className="itly-card itly-tilt"
            data-testid={`why-card-${p.n}`}
            data-reveal="up"
            style={{
                padding: "28px 28px 26px",
                display: "flex",
                flexDirection: "column",
                gap: 14,
                transition: "transform 260ms cubic-bezier(0.2, 0.7, 0.2, 1)",
            }}
        >
            <span
                style={{
                    fontFamily: "Fraunces, serif",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--itly-orange-500)",
                    letterSpacing: "0.02em",
                }}
            >
                ✦ {p.n}
            </span>
            <h3
                className="itly-serif"
                style={{
                    margin: 0,
                    fontSize: 24,
                    lineHeight: 1.15,
                    color: "var(--itly-fg-strong)",
                    letterSpacing: "-0.01em",
                }}
            >
                {t(p.titleKey)}
            </h3>
            <p
                style={{
                    margin: 0,
                    fontFamily: "Inter, sans-serif",
                    fontSize: 15,
                    lineHeight: 1.6,
                    color: "var(--itly-muted)",
                }}
            >
                {t(p.bodyKey)}
            </p>
            <Mini />
        </div>
    );
}

export default function WhyPartner() {
    const { t } = useI18n();
    return (
        <section
            id="perche"
            data-testid="why-partner"
            style={{
                position: "relative",
                background: "var(--itly-page)",
                padding: "24px 32px 96px",
            }}
        >
            <div style={{ maxWidth: 1200, margin: "0 auto" }}>
                <div style={{ textAlign: "center", maxWidth: 780, margin: "0 auto 56px" }}>
                    <p
                        data-testid="why-eyebrow"
                        style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 12.5,
                            fontWeight: 700,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: "var(--itly-orange-700)",
                            margin: 0,
                        }}
                    >
                        {t("why.badge")}
                    </p>
                    <h2
                        className="itly-serif"
                        data-testid="why-title"
                        style={{
                            marginTop: 16,
                            marginBottom: 0,
                            fontSize: "clamp(32px, 3.4vw, 46px)",
                            lineHeight: 1.1,
                            color: "var(--itly-fg-strong)",
                            letterSpacing: "-0.02em",
                        }}
                    >
                        {t("why.title_a")}{" "}
                        <em style={{ fontStyle: "italic", color: "var(--itly-orange-500)", fontWeight: 500 }}>
                            {t("why.title_b")}
                        </em>
                    </h2>
                </div>

                <div
                    className="itly-grid-3"
                    data-testid="why-grid"
                    style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 24 }}
                >
                    {PILLARS.map((p) => (
                        <PillarCard key={p.n} p={p} />
                    ))}
                </div>

                <div style={{ marginTop: 28, display: "flex", justifyContent: "center", gap: 10, fontFamily: "Inter, sans-serif", fontSize: 13.5, color: "var(--itly-muted)" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                        {t("why.foot")}
                    </span>
                </div>
            </div>
        </section>
    );
}

const miniLabelStyle = {
    fontFamily: "Inter, sans-serif",
    fontSize: 12.5,
    color: "var(--itly-muted)",
    fontWeight: 500,
};
